import { Divider, PageHeaderWrapper } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, Skeleton, Stack, ThemeProvider } from "@mui/material";

import { PageWrapper } from "../../components/PageWrapper";

const LIST_ITEM_CONTENT_INDENT = "36px";

function SkeletonListItemHeader() {
  return (
    <Stack direction="row" alignItems="center" spacing={4} sx={{ marginBottom: 4 }}>
      <Skeleton variant="circular" width={24} height={24} />
      <Skeleton variant="text" width="35%" height={24} />
    </Stack>
  );
}

function SkeletonRow(props: { labelWidth: string; valueWidth: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Skeleton variant="text" width={props.labelWidth} height={20} />
      <Skeleton variant="text" width={props.valueWidth} height={20} />
    </Stack>
  );
}

export function PastShiftDetailsSkeleton() {
  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        {/* Header skeleton */}
        <PageHeaderWrapper>
          <Box role="progressbar" aria-label="Loading past shift details">
            <Stack spacing={3} sx={{ paddingX: 6, paddingTop: 12, paddingBottom: 7 }}>
              <Skeleton variant="rounded" width={48} height={24} sx={{ borderRadius: 3 }} />

              <Skeleton variant="text" width="55%" height={36} />

              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Skeleton variant="text" width="30%" height={32} />
                <Skeleton variant="text" width="20%" height={24} />
              </Stack>
            </Stack>
          </Box>
        </PageHeaderWrapper>

        {/* Body content list */}
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            paddingX: 7,
            paddingY: 1,
            overflow: "hidden",
          })}
        >
          {/* Schedule item */}
          <Box sx={{ paddingY: 6 }}>
            <SkeletonListItemHeader />
            <Box sx={{ paddingLeft: LIST_ITEM_CONTENT_INDENT }}>
              <Skeleton variant="text" width="60%" height={22} />
              <Skeleton variant="text" width="40%" height={18} sx={{ marginTop: 1 }} />
            </Box>
          </Box>
          {/* Timesheet item */}
          <Box sx={{ paddingY: 6 }}>
            <SkeletonListItemHeader />
            <Box sx={{ paddingLeft: LIST_ITEM_CONTENT_INDENT }}>
              <Stack spacing={2}>
                <SkeletonRow labelWidth="40%" valueWidth="20%" />
                <SkeletonRow labelWidth="35%" valueWidth="20%" />
                <SkeletonRow labelWidth="20%" valueWidth="15%" />
                <SkeletonRow labelWidth="25%" valueWidth="20%" />
                <Divider sx={{ marginY: 0 }} />
                <SkeletonRow labelWidth="35%" valueWidth="25%" />
                <SkeletonRow labelWidth="40%" valueWidth="15%" />
              </Stack>
            </Box>
          </Box>

          {/* Payment History item */}
          <Box sx={{ paddingY: 6 }}>
            <SkeletonListItemHeader />
            <Box sx={{ paddingLeft: LIST_ITEM_CONTENT_INDENT }}>
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Skeleton variant="circular" width={20} height={20} />
                    <Stack>
                      <Skeleton variant="text" width={100} height={18} />
                      <Skeleton variant="text" width={130} height={14} />
                    </Stack>
                  </Stack>
                  <Skeleton variant="text" width={50} height={20} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Skeleton variant="circular" width={20} height={20} />
                    <Stack>
                      <Skeleton variant="text" width={100} height={18} />
                      <Skeleton variant="text" width={130} height={14} />
                    </Stack>
                  </Stack>
                  <Skeleton variant="text" width={50} height={20} />
                </Stack>
              </Stack>
            </Box>
          </Box>

          {/* Workplace item */}
          <Box sx={{ paddingY: 6 }}>
            <SkeletonListItemHeader />
            <Box sx={{ paddingLeft: LIST_ITEM_CONTENT_INDENT }}>
              <Skeleton variant="text" width="70%" height={22} />
              <Skeleton variant="text" width="50%" height={16} sx={{ marginTop: 1 }} />
              <Stack direction="row" alignItems="center" spacing={2} sx={{ marginTop: 2 }}>
                <Skeleton variant="text" width={80} height={16} />
                <Skeleton variant="text" width={40} height={16} />
              </Stack>
            </Box>
          </Box>

          {/* Highlights item */}
          <Box sx={{ paddingY: 6 }}>
            <SkeletonListItemHeader />
            <Box sx={{ paddingLeft: LIST_ITEM_CONTENT_INDENT }}>
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="text" width="60%" height={16} sx={{ marginTop: 1 }} />
            </Box>
          </Box>

          {/* Extra item to fill screen */}
          <Box sx={{ paddingY: 6 }}>
            <SkeletonListItemHeader />
            <Box sx={{ paddingLeft: LIST_ITEM_CONTENT_INDENT }}>
              <Stack spacing={2}>
                <SkeletonRow labelWidth="50%" valueWidth="25%" />
                <SkeletonRow labelWidth="45%" valueWidth="20%" />
              </Stack>
            </Box>
          </Box>
        </Box>
      </PageWrapper>
    </ThemeProvider>
  );
}
